import type { ReactNode } from "react";
import { copy } from "../copy";

// A person's logo, or their initial when there is none. Never a stock
// placeholder image.
export function Avatar({ username, logoUrl, size = "h-10 w-10" }: { username: string; logoUrl?: string; size?: string }) {
  return (
    <span className={`${size} shrink-0 overflow-hidden rounded-full border border-ground-edge`}>
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-full w-full object-cover object-top" />
      ) : (
        <span
          data-testid="avatar-initial"
          className="flex h-full w-full items-center justify-center bg-ground-edge font-headline uppercase text-gold"
        >
          {username.slice(0, 1)}
        </span>
      )}
    </span>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <h3 className="text-[11px] font-bold uppercase tracking-[0.2em] text-chrome-dim">{children}</h3>;
}

export function Alert({ children, tone = "error" }: { children: ReactNode; tone?: "error" | "ok" }) {
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      className={`rounded-lg border p-3 text-[12px] leading-relaxed ${
        tone === "error" ? "border-chrome-dim/40 bg-ground text-chrome" : "border-money/40 bg-money/10 text-money"
      }`}
    >
      {children}
    </p>
  );
}

export const primaryButton =
  "bf-focusable inline-flex h-11 cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-money px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep disabled:cursor-not-allowed disabled:bg-ground-edge disabled:text-chrome-dim";

export const secondaryButton =
  "bf-focusable inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-ground-edge px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-chrome-dim transition-colors hover:border-gold/50 hover:text-gold disabled:cursor-not-allowed disabled:opacity-50";

// timeAgo renders a past timestamp as its largest unit.
export function timeAgo(at: string, now = Date.now()): string {
  const t = new Date(at).getTime();
  if (!Number.isFinite(t)) return "";
  const mins = Math.floor((now - t) / 60_000);
  const a = copy.contest.ago;
  if (mins < 1) return a.now;
  if (mins < 60) return a.minutes(mins);
  if (mins < 60 * 24) return a.hours(Math.floor(mins / 60));
  return a.days(Math.floor(mins / (60 * 24)));
}
